import React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  ArrowUp,
  Paperclip,
  Square,
  X,
  StopCircle,
  Mic,
  Globe,
  Brain,
  Workflow,
  ChevronUp,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import "../chatbar-skin.css";

/* ------------------------------------------------------------------ */
/*  Drop-in stylesheet (scoped by the .pib- prefix)                    */
/* ------------------------------------------------------------------ */
const STYLE_ID = "vella-prompt-input-box-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.innerText = `
    @keyframes pib-fade-in { from { opacity: 0 } to { opacity: 1 } }
    @keyframes pib-zoom-in { from { transform: scale(.95) } to { transform: scale(1) } }
    @keyframes pib-pulse { 0%, 100% { opacity: 1 } 50% { opacity: .5 } }
    @keyframes pib-bar { 0%, 100% { transform: scaleY(1) } 50% { transform: scaleY(.4) } }
    @keyframes pib-end-dot { 0%, 100% { opacity: 0.35; transform: scale(0.8) } 50% { opacity: 1; transform: scale(1.05) } }

    .pib-scroll::-webkit-scrollbar { width: 6px; }
    .pib-scroll::-webkit-scrollbar-track { background: transparent; }
    .pib-scroll::-webkit-scrollbar-thumb { background-color: rgba(71,85,105,0.35); border-radius: 3px; }
    .pib-scroll::-webkit-scrollbar-thumb:hover { background-color: rgba(71,85,105,0.55); }

    .pib-textarea { font-family: inherit; }
    .pib-textarea:focus { outline: none; box-shadow: none; }
    .pib-textarea::placeholder { color: rgba(71, 85, 105, 0.75); }

    .pib-send-filled:hover { background: rgba(255,255,255,1); box-shadow: 0 6px 16px rgba(14,165,233,0.25); }
    .pib-send-mic:hover { background: rgba(15,23,42,0.08); color: #0F172A; }
    .pib-send-mic-rec:hover { background: rgba(239,68,68,0.12); color: #dc2626; }

    .pib-close:hover { background: #2E3033; }

    .pib-wf-item:hover { background: rgba(120,200,255,0.10); }
    .pib-wf-item:focus-visible { outline: 2px solid rgba(120,200,255,0.5); outline-offset: -2px; }
    .pib-wf-trigger:hover { background: rgba(15,23,42,0.06); }

    .pib-tooltip-content {
      z-index: 60;
      border-radius: 6px;
      border: 1px solid #333;
      background: #1F2023;
      padding: 6px 10px;
      font-size: 12px;
      color: #fff;
      box-shadow: 0 4px 10px -2px rgba(0,0,0,0.35);
      animation: pib-fade-in .12s ease;
      font-family: inherit;
    }
    .pib-dialog-overlay {
      position: fixed;
      inset: 0;
      z-index: 60;
      background: rgba(0,0,0,0.6);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      animation: pib-fade-in .2s ease;
    }
    .pib-dialog-content {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 61;
      width: 90vw;
      max-width: 800px;
      max-height: 85vh;
      overflow: hidden;
      border-radius: 18px;
      animation: pib-fade-in .2s ease, pib-zoom-in .2s ease;
      outline: none;
    }
  `;
  document.head.appendChild(el);
}

/* ------------------------------------------------------------------ */
/*  Tooltip wrappers                                                   */
/* ------------------------------------------------------------------ */
const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipContent = React.forwardRef(
  ({ className, sideOffset = 4, children, ...props }, ref) => (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={`pib-tooltip-content ${className || ""}`}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/* ------------------------------------------------------------------ */
/*  Image preview dialog                                               */
/* ------------------------------------------------------------------ */
function ImageViewDialog({ imageUrl, onClose }) {
  if (!imageUrl) return null;
  return (
    <DialogPrimitive.Root open={!!imageUrl} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="pib-dialog-overlay" />
        <DialogPrimitive.Content className="pib-dialog-content">
          <DialogPrimitive.Title
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: "hidden",
              clip: "rect(0,0,0,0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
          >
            Image Preview
          </DialogPrimitive.Title>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{
              position: "relative",
              background: "#1F2023",
              borderRadius: 14,
              overflow: "hidden",
              boxShadow: "0 20px 50px -10px rgba(0,0,0,0.5)",
            }}
          >
            <img
              src={imageUrl}
              alt="Full preview"
              style={{
                width: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                display: "block",
              }}
            />
            <DialogPrimitive.Close
              className="pib-close"
              style={{
                position: "absolute",
                right: 14,
                top: 14,
                zIndex: 2,
                borderRadius: 999,
                background: "rgba(46,48,51,0.8)",
                border: "none",
                padding: 6,
                cursor: "pointer",
                color: "#E5E7EB",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <X size={18} />
            </DialogPrimitive.Close>
          </motion.div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/* ------------------------------------------------------------------ */
/*  Voice recorder (visual)                                            */
/* ------------------------------------------------------------------ */
function VoiceRecorder({ isRecording, onStartRecording, onStopRecording, visualizerBars = 32 }) {
  const [time, setTime] = React.useState(0);
  const timerRef = React.useRef(null);
  const startedRef = React.useRef(false);
  const timeRef = React.useRef(0);

  React.useEffect(() => {
    timeRef.current = time;
  }, [time]);

  React.useEffect(() => {
    if (isRecording) {
      if (!startedRef.current) {
        startedRef.current = true;
        onStartRecording && onStartRecording();
      }
      timerRef.current = setInterval(() => setTime((t) => t + 1), 1000);
    } else {
      if (startedRef.current) {
        startedRef.current = false;
        onStopRecording && onStopRecording(timeRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTime(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line
  }, [isRecording]);

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        transition: "opacity .3s, height .3s",
        padding: "12px 0",
        opacity: isRecording ? 1 : 0,
        height: isRecording ? "auto" : 0,
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <div
          style={{
            height: 8,
            width: 8,
            borderRadius: "50%",
            background: "#ef4444",
            animation: "pib-pulse 1.4s infinite",
          }}
        />
        {/* Timer text — dark navy to match the orb family and stay readable
            on the chatbar's light glass background. (Was white before, which
            disappeared on the light surface.) */}
        <span style={{ fontFamily: "monospace", fontSize: 13, color: "rgba(10,15,74,0.85)" }}>
          {fmt(time)}
        </span>
      </div>
      <div
        style={{
          width: "100%",
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          padding: "0 16px",
        }}
      >
        {Array.from({ length: visualizerBars }).map((_, i) => (
          <div
            key={i}
            style={{
              width: 2,
              borderRadius: 999,
              // Dark navy (#0A0F4A) — same family as the VellaOrb sphere.
              // Was rgba(255,255,255,0.5) which ghosted on the light glass
              // chatbar background. 0.85 alpha keeps the bars vivid against
              // the glass without being slab-black.
              background: "rgba(10,15,74,0.85)",
              height: `${Math.max(15, Math.random() * 100)}%`,
              animation: `pib-bar ${0.5 + Math.random() * 0.5}s infinite`,
              animationDelay: `${i * 0.05}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Divider between toggle buttons — removed per design: the chat-bar    */
/*  buttons now flow together with gap spacing, no vertical lines.       */
/* ------------------------------------------------------------------ */
function CustomDivider() {
  return null;
}

/* ------------------------------------------------------------------ */
/*  Platform icons (inline SVG, no emojis)                             */
/* ------------------------------------------------------------------ */
const GoogleIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

const MetaIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 32 24" fill="none" aria-hidden="true">
    <path
      d="M3 12c0-3.4 1.9-6.2 4.7-6.2 2.2 0 3.7 1.5 5.5 4.4l1.6 2.6c.5.8.9 1.5 1.4 2.2C13.6 18.7 11.6 21 8.6 21 5 21 3 17 3 12zm14.4-1.4c-.5-.7-.9-1.4-1.4-2.2C18.4 5.3 20.4 3 23.4 3c4 0 5.6 4 5.6 9 0 3.4-1.9 6.2-4.7 6.2-2.2 0-3.7-1.5-5.5-4.4l-1.4-2.2zm-3.4 4.4c-1.1-1.6-2.6-4.4-3.6-6.4l-.6-1c1.5-2.4 3.3-4 5.6-4 3 0 5 4 5 9 0 1.4-.2 2.7-.6 3.7-.5-1-1.1-2-2-3.3l-1.4-2.2c-.6 1-1.4 2.4-2.4 4.2z"
      fill="#0866FF"
    />
  </svg>
);

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M20.4 8.2c-1.4 0-2.7-.5-3.7-1.4v8.5c0 4.2-3.4 7.7-7.7 7.7-4.2 0-7.7-3.4-7.7-7.7s3.4-7.7 7.7-7.7c.4 0 .8 0 1.2.1v3.9c-.4-.1-.8-.2-1.2-.2-2.1 0-3.9 1.7-3.9 3.9 0 2.1 1.7 3.9 3.9 3.9s3.9-1.7 3.9-3.9V0h3.8c0 2.5 2 4.5 4.5 4.5v3.7h.2z"
      fill="#25F4EE"
      transform="translate(-1 1)"
    />
    <path
      d="M21.4 7.2c-1.4 0-2.7-.5-3.7-1.4v8.5c0 4.2-3.4 7.7-7.7 7.7-4.2 0-7.7-3.4-7.7-7.7s3.4-7.7 7.7-7.7c.4 0 .8 0 1.2.1v3.9c-.4-.1-.8-.2-1.2-.2-2.1 0-3.9 1.7-3.9 3.9 0 2.1 1.7 3.9 3.9 3.9s3.9-1.7 3.9-3.9V0h3.8c0 2.5 2 4.5 4.5 4.5v2.7h.2z"
      fill="#FE2C55"
      transform="translate(1 -1)"
    />
    <path
      d="M20.9 7.7c-1.4 0-2.7-.5-3.7-1.4v8.5c0 4.2-3.4 7.7-7.7 7.7-4.2 0-7.7-3.4-7.7-7.7s3.4-7.7 7.7-7.7c.4 0 .8 0 1.2.1v3.9c-.4-.1-.8-.2-1.2-.2-2.1 0-3.9 1.7-3.9 3.9 0 2.1 1.7 3.9 3.9 3.9s3.9-1.7 3.9-3.9V0h3.8c0 2.5 2 4.5 4.5 4.5v3.2h-.8z"
      fill="#fff"
    />
  </svg>
);

const ParamountIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 2 L22 21 L2 21 Z"
      fill="#0064FF"
    />
    <path
      d="M12 6.5 L8.2 14 L11 14 L9.5 17 L14.5 11 L12 11 Z"
      fill="#fff"
      opacity="0.95"
    />
    <path
      d="M17.5 5.5l.5 1.4 1.4.2-1 1 .2 1.4-1.1-.7-1.3.7.3-1.4-1-1 1.4-.2z"
      fill="#fff"
    />
  </svg>
);

const AppleIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
      fill="#fff"
    />
  </svg>
);

const GmailIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 18" aria-hidden="true">
    <path d="M2 0h20a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2z" fill="#fff"/>
    <path d="M2 2h2v14H2zM20 2h2v14h-2z" fill="#C5221F"/>
    <path d="M2 2l10 7L22 2v3L12 12 2 5z" fill="#EA4335"/>
    <path d="M4 4l8 5.5L20 4v2L12 11 4 6z" fill="#FBBC04" opacity="0.55"/>
  </svg>
);

const PLATFORM_OPTIONS = [
  {
    id: "google",
    name: "Google",
    Icon: GoogleIcon,
    description: "Google Ads, Business Profile, Analytics",
  },
  {
    id: "meta",
    name: "Meta",
    Icon: MetaIcon,
    description: "Facebook, Instagram",
  },
  {
    id: "tiktok",
    name: "TikTok",
    Icon: TikTokIcon,
    description: "TikTok content + ads",
  },
  {
    id: "paramount",
    name: "Paramount",
    Icon: ParamountIcon,
    description: "Paramount Ads Manager",
  },
  {
    id: "apple",
    name: "Apple",
    Icon: AppleIcon,
    description: "Search Ads, Business Connect",
  },
  {
    id: "gmail",
    name: "Gmail",
    Icon: GmailIcon,
    description: "Email marketing",
  },
];

const PLATFORM_BY_ID = PLATFORM_OPTIONS.reduce((acc, p) => {
  acc[p.id] = p;
  return acc;
}, {});

/* ------------------------------------------------------------------ */
/*  Checkbox glyph (inline SVG)                                        */
/* ------------------------------------------------------------------ */
function CheckboxGlyph({ checked, accent = "#0EA5E9" }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 16,
        height: 16,
        flexShrink: 0,
        borderRadius: 4,
        border: `1.5px solid ${checked ? accent : "rgba(148,163,184,0.55)"}`,
        background: checked ? accent : "transparent",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background .15s, border-color .15s",
      }}
    >
      {checked && (
        <svg
          width={11}
          height={11}
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.5 6.2 L5 8.6 L9.5 3.6"
            stroke="#fff"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Workflows menu (popover above the trigger button) -- multi-select  */
/* ------------------------------------------------------------------ */
function WorkflowsMenu({
  open,
  onClose,
  activeWorkflows,
  onTogglePlatform,
  onSelectAuto,
  onSelectAll,
}) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const isAuto = !activeWorkflows || activeWorkflows.length === 0;
  const allSelected =
    activeWorkflows && activeWorkflows.length === PLATFORM_OPTIONS.length;

  const Divider = () => (
    <div
      style={{
        height: 1,
        background: "rgba(148,163,184,0.18)",
        margin: "6px 4px",
      }}
    />
  );

  const Row = ({ checked, label, sublabel, leading, onClick, accent }) => (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      onClick={onClick}
      className="pib-wf-item"
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        background: "transparent",
        border: "none",
        borderRadius: 8,
        color: "#E5E7EB",
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 13.5,
        fontWeight: 500,
        textAlign: "left",
        transition: "background .15s",
      }}
    >
      <CheckboxGlyph checked={checked} accent={accent || "#0EA5E9"} />
      {leading && (
        <span
          style={{
            width: 22,
            height: 22,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {leading}
        </span>
      )}
      <span style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <span style={{ color: "#E5E7EB", lineHeight: 1.2 }}>{label}</span>
        {sublabel && (
          <span
            style={{
              color: "rgba(148,163,184,0.85)",
              fontSize: 11.5,
              fontWeight: 400,
              lineHeight: 1.3,
              marginTop: 2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {sublabel}
          </span>
        )}
      </span>
    </button>
  );

  return (
    <div
      ref={ref}
      role="menu"
      style={{
        // Open UPWARD — float above the chat bar instead of covering the
        // centered orb / action chips below it.
        position: "absolute",
        bottom: "100%",
        left: 0,
        marginBottom: 8,
        zIndex: 60,
        minWidth: 280,
        // Cap the upward growth so a long platform list scrolls inside the
        // popover rather than climbing into the orb in the center.
        maxHeight: "min(56vh, 420px)",
        overflowY: "auto",
        overflowX: "hidden",
        padding: 6,
        borderRadius: 14,
        border: "1px solid rgba(120,200,255,0.22)",
        background: "rgba(15,23,42,0.85)",
        backdropFilter: "blur(20px) saturate(160%)",
        WebkitBackdropFilter: "blur(20px) saturate(160%)",
        boxShadow:
          "0 18px 40px rgba(0,0,0,0.45), inset 0 0 30px rgba(75,200,255,0.08)",
        animation: "pib-fade-in .15s ease, pib-zoom-in .15s ease",
        transformOrigin: "bottom left",
      }}
    >
      <Row
        checked={isAuto}
        label="Auto"
        sublabel="Vella detects the right platforms"
        accent="#10B981"
        onClick={onSelectAuto}
      />
      <Row
        checked={allSelected}
        label="Select All"
        sublabel="Run across every connected platform"
        accent="#8B5CF6"
        onClick={onSelectAll}
      />
      <Divider />
      {PLATFORM_OPTIONS.map(({ id, name, Icon, description }) => (
        <Row
          key={id}
          checked={!!(activeWorkflows && activeWorkflows.includes(id))}
          label={name}
          sublabel={description}
          leading={<Icon size={16} />}
          onClick={() => onTogglePlatform(id)}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Toggle chip (Search / Think)                                       */
/* ------------------------------------------------------------------ */
function ToggleChip({ active, onClick, Icon, label, activeColor, activeBg, activeBorder }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-no-vella-ring="true"
      style={{
        borderRadius: 999,
        transition: "all .2s",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 7px",
        height: 26,
        background: active ? activeBg : "transparent",
        border: `1px solid ${active ? activeBorder : "transparent"}`,
        color: active ? activeColor : "#475569",
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <motion.div
          animate={{ rotate: active ? 360 : 0, scale: active ? 1.1 : 1 }}
          whileHover={{ rotate: active ? 360 : 15, scale: 1.1 }}
          transition={{ type: "spring", stiffness: 260, damping: 25 }}
          style={{ display: "flex" }}
        >
          <Icon size={15} color={active ? activeColor : "currentColor"} />
        </motion.div>
      </div>
      <AnimatePresence initial={false}>
        {active && (
          <motion.span
            key="label"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "auto", opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              fontSize: 11.5,
              overflow: "hidden",
              whiteSpace: "nowrap",
              color: activeColor,
              flexShrink: 0,
            }}
          >
            {label}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */
const isImageFile = (file) => file && file.type && file.type.startsWith("image/");

export const PromptInputBox = React.forwardRef(function PromptInputBox(props, ref) {
  const {
    onSend = () => {},
    onStop = () => {},
    isLoading = false,
    disabled = false,
    placeholder = "Ask Vella anything...",
    initialValue = "",
    style,
    autoFocus = false,
    showToggles = true,
    onStartVoice,
    onStopVoice,
    // When the parent owns a real voice session (LiveKit room, etc.), pass
    // its live state in. The mic button morphs into a blue "● ● ● End" pill
    // that calls onStopVoice when tapped. Falls back to the local toggle
    // (used by browser-SR consumers like the Web Studio dictation input)
    // when the parent doesn't drive it.
    voiceActive = false,
    // ChatGPT-thick variant — bumps outer padding + textarea min-height so
    // the bar reads as a primary surface (Home page) rather than a dock.
    taller = false,
  } = props;

  const [input, setInput] = React.useState(initialValue);
  const [files, setFiles] = React.useState([]);
  const [filePreviews, setFilePreviews] = React.useState({});
  const [selectedImage, setSelectedImage] = React.useState(null);
  const [isRecording, setIsRecording] = React.useState(false);
  // Modifier toggles intentionally default OFF. The placeholder only flips
  // to "Search the web..." or "Think deeply..." when the user explicitly
  // clicks the corresponding chip below the textarea.
  const [showSearch, setShowSearch] = React.useState(false);
  const [showThink, setShowThink] = React.useState(false);
  const [showWorkflowsMenu, setShowWorkflowsMenu] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [activeWorkflows, setActiveWorkflows] = React.useState([]);

  const uploadInputRef = React.useRef(null);
  const textareaRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    clear: () => {
      setInput("");
      setFiles([]);
      setFilePreviews({});
    },
    setValue: (v) => setInput(v),
  }));

  const minTextareaHeight = taller ? 56 : 32;
  React.useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height =
      Math.max(minTextareaHeight, Math.min(textareaRef.current.scrollHeight, 240)) + "px";
  }, [input, minTextareaHeight]);

  React.useEffect(() => {
    if (autoFocus && textareaRef.current) textareaRef.current.focus();
  }, [autoFocus]);

  const processFile = (file) => {
    if (!isImageFile(file)) return;
    if (file.size > 10 * 1024 * 1024) return;
    setFiles([file]);
    const reader = new FileReader();
    reader.onload = (e) => setFilePreviews({ [file.name]: e.target?.result });
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = () => {
    setFiles([]);
    setFilePreviews({});
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = Array.from(e.dataTransfer.files || []).filter(isImageFile);
    if (dropped.length) processFile(dropped[0]);
  };

  const handlePaste = React.useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const f = items[i].getAsFile();
        if (f) {
          e.preventDefault();
          processFile(f);
          break;
        }
      }
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [handlePaste]);

  const hasContent = (input && input.trim() !== "") || files.length > 0;

  const handleSubmit = () => {
    if (!hasContent) return;
    let prefix = "";
    if (showSearch) prefix = "[Search: ";
    else if (showThink) prefix = "[Think: ";
    const text = prefix ? `${prefix}${input}]` : input;
    onSend(text, files, { activeWorkflows: [...activeWorkflows] });
    setInput("");
    setFiles([]);
    setFilePreviews({});
  };

  // When the parent passes onStartVoice/onStopVoice it owns voice state
  // (LiveKit room lifecycle, etc.). The chatbar input layout stays put and
  // only the mic button morphs into a blue "● ● ● End" pill.
  const parentDrivesVoice = !!(onStartVoice || onStopVoice);
  // The bouncing-bars VoiceRecorder overlay only makes sense for the
  // browser-SR fallback path. For parent-driven (LiveKit) mode we keep the
  // input layout intact.
  const showLocalRecording = isRecording && !parentDrivesVoice;
  // Effective "voice is on" — either the parent says so, or local SR is
  // toggled. Drives mic button styling.
  const effectiveVoiceActive = parentDrivesVoice ? !!voiceActive : isRecording;

  const handleMicClick = () => {
    if (isLoading) return;
    if (parentDrivesVoice) {
      // Don't manage local isRecording — parent's voiceActive prop is the
      // source of truth and may stay false until the room is connected.
      if (voiceActive) onStopVoice && onStopVoice();
      else onStartVoice && onStartVoice();
      return;
    }
    // Fallback: built-in webkitSpeechRecognition
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setIsRecording((r) => !r);
      return;
    }
    if (isRecording) {
      setIsRecording(false);
      return;
    }
    setIsRecording(true);
    try {
      const rec = new SR();
      rec.lang = "en-US";
      rec.interimResults = true;
      rec.continuous = false;
      rec.onresult = (ev) => {
        const r = ev.results[ev.results.length - 1];
        if (r.isFinal) {
          setInput((prev) => (prev ? prev + " " : "") + r[0].transcript);
        }
      };
      rec.onend = () => setIsRecording(false);
      rec.onerror = () => setIsRecording(false);
      rec.start();
    } catch {
      setIsRecording(false);
    }
  };

  const handleToggle = (which) => {
    if (which === "search") {
      setShowSearch((v) => !v);
      setShowThink(false);
    } else if (which === "think") {
      setShowThink((v) => !v);
      setShowSearch(false);
    }
  };

  const togglePlatform = (id) => {
    setActiveWorkflows((prev) => {
      const next = prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id];
      return next;
    });
  };

  const selectAuto = () => setActiveWorkflows([]);
  const selectAllPlatforms = () =>
    setActiveWorkflows(PLATFORM_OPTIONS.map((p) => p.id));
  const removeWorkflow = (id) =>
    setActiveWorkflows((prev) => prev.filter((p) => p !== id));

  const workflowPlaceholder =
    activeWorkflows.length === 0
      ? null
      : activeWorkflows.length === 1
      ? `Working in ${PLATFORM_BY_ID[activeWorkflows[0]]?.name || "platform"}...`
      : `Working across ${activeWorkflows.length} platforms...`;

  const placeholderText = showThink
    ? "Think deeply..."
    : workflowPlaceholder || placeholder;

  return (
    <TooltipProvider delayDuration={200}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={handleDrop}
        style={{
          // OPERATION SLIPCOVER — the outer element is now just a transparent
          // stacking wrapper. The frosted-glass pill styling has moved onto the
          // inner control row via the `.vella-chatbar` skin (chatbar-skin.css).
          // File previews / voice recorder stack ABOVE the pill.
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          boxSizing: "border-box",
          fontFamily: "inherit",
          ...style,
        }}
      >
        {/* File previews */}
        {files.length > 0 && !showLocalRecording && (
          <div
            style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: 0, paddingBottom: 4 }}
          >
            {files.map((file, i) => (
              <div key={i} style={{ position: "relative" }}>
                {file.type.startsWith("image/") && filePreviews[file.name] && (
                  <div
                    onClick={() => setSelectedImage(filePreviews[file.name])}
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: 14,
                      overflow: "hidden",
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    <img
                      src={filePreviews[file.name]}
                      alt={file.name}
                      style={{ height: "100%", width: "100%", objectFit: "cover" }}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile();
                      }}
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        borderRadius: 999,
                        background: "rgba(0,0,0,0.7)",
                        padding: 2,
                        border: "none",
                        cursor: "pointer",
                        color: "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Recording UI — only for the browser-SpeechRecognition fallback.
            Parent-driven (LiveKit) voice keeps the input layout intact;
            the mic button itself morphs into the End pill instead. */}
        {showLocalRecording && (
          <VoiceRecorder
            isRecording={showLocalRecording}
            onStartRecording={() => {}}
            onStopRecording={() => {}}
          />
        )}

        {/* Single-row layout: [left buttons] [textarea fills] [mic] [send].
            OPERATION SLIPCOVER — this row IS the pill now (vella-chatbar skin).
            `hastext` turns the send arrow blue + adds its lift shadow. */}
        <div className={`vella-chatbar${hasContent ? " hastext" : ""}`}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              gap: 4,
              transition: "opacity .3s",
              opacity: showLocalRecording ? 0 : 1,
              visibility: showLocalRecording ? "hidden" : "visible",
              height: showLocalRecording ? 0 : "auto",
            }}
          >
            {/* Attach */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => uploadInputRef.current?.click()}
                  disabled={showLocalRecording}
                  className={`ico attach${files.length > 0 ? " sel" : ""}`}
                  data-no-vella-ring="true"
                >
                  <Paperclip size={16} />
                  <input
                    ref={uploadInputRef}
                    type="file"
                    style={{ display: "none" }}
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0)
                        processFile(e.target.files[0]);
                      if (e.target) e.target.value = "";
                    }}
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent>Attach image</TooltipContent>
            </Tooltip>

            {showToggles && (
              <div style={{ display: "flex", alignItems: "center" }}>
                {/* Search (web) — same handleToggle('search') handler drives
                    showSearch → the [Search: …] submit prefix. */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => handleToggle("search")}
                      aria-label="Search the web"
                      aria-pressed={showSearch}
                      className={`ico web${showSearch ? " sel" : ""}`}
                      data-no-vella-ring="true"
                    >
                      <Globe />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Search the web</TooltipContent>
                </Tooltip>
                {/* Think (brain) — same handleToggle('think') handler drives
                    showThink → the [Think: …] prefix + "Think deeply…" hint. */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => handleToggle("think")}
                      aria-label="Think"
                      aria-pressed={showThink}
                      className={`ico brain${showThink ? " sel" : ""}`}
                      data-no-vella-ring="true"
                    >
                      <Brain />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Think deeply</TooltipContent>
                </Tooltip>
                <div style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <button
                    type="button"
                    onClick={() => setShowWorkflowsMenu((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={showWorkflowsMenu}
                    aria-label="Workflows"
                    title="Workflows"
                    className={`wf${showWorkflowsMenu ? " sel" : ""}`}
                    data-no-vella-ring="true"
                  >
                    <Workflow />
                    {/* Item 11 — label noise: icon + tooltip only (text dropped).
                        Chevron rotation is CSS-driven via `.wf.sel .chev`. */}
                    <span className="chev">
                      <ChevronUp />
                    </span>
                  </button>

                  {activeWorkflows.length > 0 && (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        marginLeft: 4,
                        flexWrap: "wrap",
                        maxWidth: 280,
                      }}
                    >
                      {activeWorkflows.map((id) => {
                        const platform = PLATFORM_BY_ID[id];
                        if (!platform) return null;
                        const Icon = platform.Icon;
                        return (
                          <span
                            key={id}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "3px 4px 3px 7px",
                              height: 24,
                              borderRadius: 999,
                              background: "rgba(14,165,233,0.12)",
                              border: "1px solid rgba(14,165,233,0.35)",
                              color: "#0F172A",
                              fontFamily: "inherit",
                              fontSize: 11,
                              fontWeight: 600,
                              lineHeight: 1,
                            }}
                          >
                            <Icon size={12} />
                            <span>{platform.name}</span>
                            <button
                              type="button"
                              aria-label={`Remove ${platform.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeWorkflow(id);
                              }}
                              style={{
                                width: 14,
                                height: 14,
                                marginLeft: 1,
                                borderRadius: 999,
                                border: "none",
                                background: "transparent",
                                color: "#0F172A",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: 0.7,
                              }}
                            >
                              <X size={10} />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  <WorkflowsMenu
                    open={showWorkflowsMenu}
                    onClose={() => setShowWorkflowsMenu(false)}
                    activeWorkflows={activeWorkflows}
                    onTogglePlatform={togglePlatform}
                    onSelectAuto={selectAuto}
                    onSelectAll={selectAllPlatforms}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Inline textarea — sits between left button group and right send/mic.
              OPERATION SLIPCOVER — wrapped in `.inputwrap` (flex:1). The skin
              owns colour / size / padding; only the caret, auto-grow floor/cap,
              and the recording fade stay inline. */}
          <div className="inputwrap">
            <textarea
              ref={textareaRef}
              data-vella-chatbar="true"
              className="pib-textarea pib-scroll"
              rows={1}
              value={input}
              placeholder={placeholderText}
              disabled={disabled || isLoading || showLocalRecording}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              style={{
                caretColor: "#0EA5E9",
                fontWeight: 500,
                lineHeight: taller ? 1.5 : 1.45,
                minHeight: taller ? minTextareaHeight : 26,
                maxHeight: taller ? 200 : 120,
                opacity: showLocalRecording ? 0 : 1,
                transition: "opacity .2s",
              }}
            />
          </div>

          {/* Mic / End-pill — whenever a voice session is active (parent-
              driven LiveKit OR local browser-SR fallback) the round mic
              button morphs into a blue "● ● ● End" pill. The text input
              stays in place — no overlay. */}
          {effectiveVoiceActive ? (
            <button
              type="button"
              onClick={() => {
                if (isLoading) return;
                // Parent-driven (LiveKit): hand off to onStopVoice. Local
                // fallback: also flip our internal isRecording flag so the
                // mic icon comes back on the next click.
                if (!parentDrivesVoice) setIsRecording(false);
                if (onStopVoice) onStopVoice();
              }}
              disabled={isLoading}
              aria-label="End voice call"
              className="pib-end-pill"
              style={{
                height: 30,
                padding: "0 12px 0 10px",
                borderRadius: 14,
                border: "none",
                cursor: isLoading ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "#3B82F6",
                color: "#FFFFFF",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.01em",
                boxShadow: "0 2px 8px rgba(59,130,246,0.35)",
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              <span aria-hidden="true" style={{ display: "inline-flex", gap: 3 }}>
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      background: "#FFFFFF",
                      animation: `pib-end-dot 1.2s infinite ${i * 0.2}s`,
                    }}
                  />
                ))}
              </span>
              End
            </button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => {
                    if (isLoading) return;
                    handleMicClick();
                  }}
                  disabled={isLoading}
                  className="ico mic"
                  data-no-vella-ring="true"
                  style={{
                    cursor: isLoading ? "not-allowed" : "pointer",
                    opacity: isLoading ? 0.5 : 1,
                  }}
                >
                  <Mic size={16} />
                </button>
              </TooltipTrigger>
              <TooltipContent>Voice message</TooltipContent>
            </Tooltip>
          )}

          {/* Send / Stop */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  // While generating, this button IS the stop control.
                  if (isLoading) { onStop(); return; }
                  if (hasContent) {
                    handleSubmit();
                    // OPERATION SLIPCOVER — send burst: one detached ring node
                    // per send, self-removing ≤950ms. No React handlers on it.
                    const btn = e.currentTarget;
                    if (btn) {
                      const ring = document.createElement("div");
                      ring.className = "sendring";
                      btn.appendChild(ring);
                      setTimeout(() => ring.remove(), 950);
                    }
                  }
                }}
                disabled={!isLoading && !hasContent}
                aria-label={isLoading ? "Stop generation" : "Send message"}
                className="send"
                data-no-vella-ring="true"
              >
                {isLoading ? (
                  <Square size={12} fill="#1A47F0" stroke="#1A47F0" style={{ animation: "pib-pulse 1.4s infinite" }} />
                ) : (
                  <ArrowUp size={14} />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>{isLoading ? "Stop generation" : "Send message"}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <ImageViewDialog imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />
    </TooltipProvider>
  );
});

export default PromptInputBox;
